/** Formatting and wording helpers for the leadership briefing page (plain values, no HTML strings). */

export const DASH = '—';

/** Category swatch colours, matching the chart legend. */
export const CAT_SWATCH = {
  'Service unavailable': '#1a1a1a',
  'Files, upload, download, preview': '#ff8000',
  'Access, login, IP whitelist': '#c45000',
  'iSheet, search and Q&A': '#8a4b08',
  'Workflow, tasks and email': '#d4a574',
  'Migration and integrations': '#5c5c5c',
  'Reporting and other': '#e0d5c8'
};

/** Symptom filter keys, in legend order. */
export const SYMPTOMS = [
  { key: 'down', label: 'Service unavailable' },
  { key: 'files', label: 'Files, upload, download, preview' },
  { key: 'access', label: 'Access, login, IP whitelist' },
  { key: 'modules', label: 'iSheet, search and Q&A' },
  { key: 'process', label: 'Workflow, tasks and email' },
  { key: 'migr', label: 'Migration and integrations' },
  { key: 'other', label: 'Reporting and other' }
];

const isNum = (value) => value != null && value !== '' && Number.isFinite(Number(value));

export const fmt1 = (value) => (isNum(value) ? Number(value).toFixed(1) : DASH);
export const fmtInt = (value) => (isNum(value) ? String(Math.round(Number(value))) : DASH);

export const fmtDay = (iso) => {
  if (!iso) return DASH;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return DASH;
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
};

/** "1 January" style day without the year, used in the period lines. */
export const fmtDayLong = (iso, withYear = false) => {
  const date = iso ? new Date(iso) : null;
  if (!date || Number.isNaN(date.getTime())) return DASH;
  return date.toLocaleDateString('en-GB', {
    day: 'numeric', month: 'long', ...(withYear ? { year: 'numeric' } : {}), timeZone: 'UTC'
  });
};

/** "good" / "bad" / '' for a change, given which direction is better for the service. */
export const toneOf = (change, betterWhen = 'lower') => {
  if (change == null || Number(change) === 0 || !Number.isFinite(Number(change))) return '';
  const n = Number(change);
  const improved = betterWhen === 'higher' ? n > 0 : n < 0;
  return improved ? 'good' : 'bad';
};

/** Tone for a before/after pair. */
export const pairTone = (prev, next, betterWhen = 'lower') => {
  if (!isNum(prev) || !isNum(next)) return '';
  const change = Math.round((Number(next) - Number(prev)) * 10) / 10;
  return toneOf(change, betterWhen);
};

export const signed1 = (value) => (isNum(value) ? `${Number(value) > 0 ? '+' : ''}${fmt1(value)}` : DASH);

export const plural = (n, one, many) => (Number(n) === 1 ? one : many);

export const openCountText = (count) => {
  if (count === 0) return 'No cases are open.';
  if (count === 1) return '1 case is still open.';
  return `${count} cases are still open.`;
};

/** Status text for an open case: "Open · L2" or "Open · L3 closed; case still open". */
export const shortOpenStatus = (row) => {
  const raw = String((row && row.statusText) || '').replace(/\s+/g, ' ').trim();
  let tier = String((row && row.location) || '').toUpperCase();
  if (tier !== 'L1' && tier !== 'L2' && tier !== 'L3') {
    const match = raw.match(/\b(L[123])\b/i);
    tier = match ? match[1].toUpperCase() : 'L1';
  }
  const state = String((row && row.adoState) || '').trim();
  const legacy = !raw
    || /Open,\s*case\s+\d+/i.test(raw)
    || /appears to be related/i.test(raw)
    || /\bis investigating\b/i.test(raw)
    || /\bL[123]\s+Done\b/i.test(raw)
    || (raw.length > 72 && !/^Open\s*[·]\s*L[123]\b/i.test(raw));
  if (!legacy && /^Open\s*[·]\s*L[123]\b/i.test(raw)) return raw;
  if (/done|closed|resolved|completed/i.test(state) || /\bL[123]\s+Done\b/i.test(raw) || /\bDone\b/i.test(raw)) {
    return `Open · ${tier} closed; case still open`;
  }
  if (state && !/^(activ(e|ity)?|in\s*progress|new|to\s*do|proposed|investigat)/i.test(state)) {
    return `Open · ${tier} · ${state}`;
  }
  return `Open · ${tier}`;
};

/** Latest detail for an open case with mail prefixes and ticket references removed. */
export const cleanOpenDetail = (row) => {
  let raw = String((row && (row.latestDetail || row.adoTitle || row.snowShortDescription || row.subject)) || '')
    .replace(/\s+/g, ' ')
    .trim();
  raw = raw.replace(/\[\s*ref\s*:[^\]]*\]/gi, ' ').replace(/\bref\s*:\s*!\S+/gi, ' ');
  while (/^(re|fw|fwd):\s*/i.test(raw) || /^(\[EXT\]\s*)+/i.test(raw)) {
    raw = raw.replace(/^(re|fw|fwd):\s*/i, '').replace(/^(\[EXT\]\s*)+/i, '').trim();
  }
  raw = raw.replace(/\[EXT\]/gi, ' ');
  while (/^Case\s*#?\s*\d+\s*:\s*/i.test(raw)) raw = raw.replace(/^Case\s*#?\s*\d+\s*:\s*/i, '').trim();
  raw = raw.replace(/\bCase\s*#\s*\d+\s*:\s*/gi, ' ').replace(/\[\s*HELP[^\]]*\]/gi, ' ');
  raw = raw.replace(/\[\s*\]/g, ' ').replace(/\s+/g, ' ').trim();
  if (!raw || raw.length < 8) return (row && row.topic) || DASH;
  return raw;
};

/** Start and end lines for a "when" cell: one day, or a start and an end day. */
export const whenLines = (row) => {
  const start = fmtDay(row.when || row.opened);
  const end = row.whenEnd ? fmtDay(row.whenEnd) : '';
  return end && end !== start && end !== DASH ? [start, end] : [start];
};

/** Period line under the page title, built from the briefing's windows. */
export const periodLine = (periods, mode) => {
  if (!periods) return '';
  // The exact windows are in the notes under Year on year, so this view's header names only the two years
  if (mode === 'yr') return `${new Date(periods.y25.start).getUTCFullYear()} and ${new Date(periods.y26.start).getUTCFullYear()}`;
  const y26 = `${fmtDayLong(periods.y26.start)} – ${fmtDayLong(periods.y26.end, true)}`;
  return `${y26} · Azure cutover ${fmtDayLong(periods.azureCutover, true)}`;
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const utcDay = (iso) => new Date(iso);
const dayMonth = (date) => `${date.getUTCDate()} ${MONTHS[date.getUTCMonth()]}`;

/** "20 Jan – 31 Mar", or "1–19 Oct" when both days fall in one month. */
const dayRange = (from, to) => (from.getUTCMonth() === to.getUTCMonth()
  ? `${from.getUTCDate()}\u2013${dayMonth(to)}`
  : `${dayMonth(from)} \u2013 ${dayMonth(to)}`);

/** Register quarter choices for the 2026 window: Q1 · 20 Jan – 31 Mar ... Q4 · 1–19 Oct. */
export const quarterOptions = (periods) => {
  if (!periods) return [];
  const start = utcDay(periods.y26.start);
  const end = utcDay(periods.y26.end);
  const year = start.getUTCFullYear();
  return [0, 1, 2, 3].map((q) => {
    const from = new Date(Math.max(Date.UTC(year, q * 3, 1), start.getTime()));
    const to = new Date(Math.min(Date.UTC(year, q * 3 + 3, 0), end.getTime()));
    return from <= to ? { key: `q${q + 1}`, label: `Q${q + 1} \u00b7 ${dayRange(from, to)}` } : null;
  }).filter(Boolean);
};

/** Where the periods stop and start, e.g. "December 2025 stops on 24 December. January 2026 starts on 20 January." */
export const windowEdgeNote = (periods) => {
  const long = (iso) => {
    const date = utcDay(iso);
    return { month: date.toLocaleDateString('en-GB', { month: 'long', timeZone: 'UTC' }), year: date.getUTCFullYear(), day: date.getUTCDate() };
  };
  const a = long(periods.y25.end);
  const b = long(periods.y26.start);
  const c = long(periods.y26.end);
  return `${a.month} ${a.year} stops on ${a.day} ${a.month}. ${b.month} ${b.year} starts on ${b.day} ${b.month}. `
    + `${c.month} ${c.year} stops on ${c.day} ${c.month}.`;
};
